import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { FormFeatureDirective } from '../form-feature.directive';
import { IFormFeatureEmbedded } from '../../../_shared/models/form/form-feature-embedded.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ITrackingEventGtm } from '../../../_shared/models/tracking/tracking-event.model';
import { GtmService } from '../../../_shared/services/gtm.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { CookieService } from 'ngx-cookie-service';
import { ICustomWindow, WindowRefService } from '../../../_shared/services/window-ref.service';
import { LinkService } from '../../../_shared/services/link.service';
import { FormFeatureBaseComponent } from '../form-feature-base/form-feature-base.component';

const IFRAME_HEIGHT_PATTERN = /^\d+(?:\.\d+)?px$/;
const MAX_IFRAME_HEIGHT_PX = 100_000;
type FormInitValue = Readonly<{ name: string; value: string | boolean }>;

@Component({
    selector: 'sirius-form-feature-embedded',
    templateUrl: './form-feature-embedded.component.html',
    styleUrls: ['./form-feature-embedded.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [FormFeatureBaseComponent],
    host: {
        '(window:message)': 'onMessage($event)',
    },
})
export class FormFeatureEmbeddedComponent extends FormFeatureDirective implements OnInit, AfterViewInit {
    private _sanitizer = inject(DomSanitizer);
    private _renderer = inject(Renderer2);
    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);
    private _cookieService = inject(CookieService);
    private _windowRef = inject(WindowRefService);

    @Input() config?: IFormFeatureEmbedded;

    formHtml?: SafeHtml;

    @ViewChild('form', { read: ElementRef }) private _form!: ElementRef;
    @ViewChild('submitTarget', { read: ElementRef }) private _submitTarget!: ElementRef<HTMLElement>;

    private _initValues: readonly FormInitValue[] = [];
    private _embedCode = '';
    private _embeddedReady = false;
    private _pendingHeight?: string;
    private _iFrame?: HTMLIFrameElement;

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    onMessage(e: MessageEvent<unknown>): void {
        const iframeWindow = this._iFrame?.contentWindow;
        if (!iframeWindow || e.source !== iframeWindow || !this.isRecord(e.data)) return;

        const tracking = e.data['tracking'];
        if (tracking) this.addTracking(tracking as ITrackingEventGtm);

        const trackingSnowplow = e.data['trackingSnowplow'];
        if (this.isRecord(trackingSnowplow) && typeof trackingSnowplow['name'] === 'string') {
            this._snowplowService.track(trackingSnowplow['name'], trackingSnowplow['data']);
        }

        const styles = e.data['styles'];
        if (styles) this.setStyling(styles);

        const done = e.data['done'];
        if (this.isRecord(done) && done['aeForm'] === true) this.onEmbeddedReady();

        if (e.data['scrollToAnchor'] === true) this._submitTarget.nativeElement.scrollIntoView();
    }

    ngOnInit(): void {
        if (this.config) {
            this.title = this.config.title ?? '';
            this.textHtml = this.config.textHtml ?? '';
            this._initValues = (this.config.form.initValues?.formFields ?? []).map(({ name, value }) => ({
                name,
                value,
            }));
            this._embedCode = this.config.form.embedCode ?? '';
        }

        this.formHtml = this._sanitizer.bypassSecurityTrustHtml(this._embedCode);

        if (!this.Window) return;

        this._initValues = [
            ...this._initValues,
            { name: 'Form_Submission_URL', value: this.Window.location.href },
            ...this.getUtmParams(),
        ];
    }

    ngAfterViewInit(): void {
        this._iFrame = (this._form.nativeElement as HTMLElement).querySelector('iframe') ?? undefined;
    }

    private getUtmParams(): FormInitValue[] {
        let utm: { name: string; value: string }[] | undefined = [];

        const cookie = this._cookieService.get('referer_info');
        if (cookie) {
            utm = JSON.parse(cookie);
        } else {
            utm = this._linkService.getUtmParams();
        }

        return utm ?? [];
    }

    private onEmbeddedReady(): void {
        this._embeddedReady = true;
        this.applyPendingHeight();
        this.postInitValues();
    }

    private setStyling(styles: unknown): void {
        if (!styles || typeof styles !== 'object' || !('height' in styles)) return;

        const heightValue = (styles as Record<string, unknown>)['height'];
        if (typeof heightValue !== 'string') return;

        if (!IFRAME_HEIGHT_PATTERN.test(heightValue)) return;

        const height = Number.parseFloat(heightValue);
        if (!Number.isFinite(height) || height <= 0 || height > MAX_IFRAME_HEIGHT_PX) return;

        this._pendingHeight = `${Math.ceil(height)}px`;
        this.applyPendingHeight();
    }

    private applyPendingHeight(): void {
        if (!this._embeddedReady || !this._pendingHeight || !this._iFrame) return;
        if (this._iFrame.style.height === this._pendingHeight) return;

        this._renderer.setStyle(this._iFrame, 'height', this._pendingHeight);
    }

    private postInitValues(): void {
        if (!this._embeddedReady || !this._initValues.length) return;

        this._iFrame?.contentWindow?.postMessage({ initValues: this._initValues }, '*');
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    //#region tracking
    private addTracking(e: ITrackingEventGtm) {
        if (!e) return;

        this._gtmService.track(e);
    }
    //#endregion
}
